import React from 'react';

import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { IPickerAspectOption } from '@rbi-ctg/menu';
import { ListCardGroup } from 'components/list-card-group';
import { FallbackImageSvg } from 'components/svgs/fallback-image';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useFormatCalories } from 'hooks/use-format-calories';
import { MenuAcceleratorBadge } from 'pages/menu/components/menu-accelerator-badge';
import { useProductWizardContext } from 'state/product-wizard-v2';
import {
  PickerAspectCalorieMap,
  formattedCalorieMapForPickerAspectCalorieMap,
  reducePickerCalorieMapToRanges,
} from 'utils/menu/picker-calorie-ranges';

import { FIRST_INDEX } from './constants';
import { PickerListItem } from './picker-list-item';
import { PickerAspectGroupContainer } from './styled';
import { IPickerAspectGroupProps } from './types';

export const PickerAspectGroup = ({
  aspectIndex,
  calorieMap,
  handlePickerAspectChange,
  pickerAspect,
  userSelectedPickerAspects,
  imageForPickerAspectOption,
  priceForPickerAspectOption,
  badgeForPickerAspectOption,
}: IPickerAspectGroupProps) => {
  const { formatMessage } = useIntl();
  const { formatCalorieRange } = useMenuCalories();
  const formatCalories = useFormatCalories();
  const { pickerAspectsUpdate } = useProductWizardContext();

  const foundPickerAspectUpdate = pickerAspectsUpdate.find(pickerAspectUpdateVal => {
    return pickerAspect._id === pickerAspectUpdateVal.id && !!pickerAspectUpdateVal.isDirty;
  });

  const [selectedOption, setSelectedOption] = React.useState<string>(
    foundPickerAspectUpdate ? userSelectedPickerAspects?.[pickerAspect._id] : ''
  );

  const showImages = pickerAspect.pickerAspectOptions.some(option => !!option.image);

  const getCalorieMapForCurrentPickerAspect = React.useCallback(
    (index: number) => {
      const calorieMapForPickerAspect = Object.values(userSelectedPickerAspects || {})
        .slice(0, index)
        .reduce((map, identifier) => (map ? map[identifier] : null), calorieMap);

      const calorieRangesForPickerAspect = reducePickerCalorieMapToRanges(
        calorieMapForPickerAspect as PickerAspectCalorieMap
      );

      return formattedCalorieMapForPickerAspectCalorieMap(
        calorieRangesForPickerAspect,
        formatCalories
      );
    },
    [calorieMap, formatCalories, userSelectedPickerAspects]
  );

  const handleOnProductClick = React.useCallback(
    (optionIdentifier: string) => {
      if (optionIdentifier === selectedOption) {
        return;
      }
      const prevOption = selectedOption;
      setSelectedOption(optionIdentifier);

      handlePickerAspectChange({
        args: {
          aspect: pickerAspect,
          optionIdentifier,
          index: aspectIndex,
        },
        onCancel: () => setSelectedOption(prevOption),
      });
    },
    [aspectIndex, handlePickerAspectChange, pickerAspect, selectedOption]
  );

  // Picker aspect options accessibility role and label.
  const optionLabel = formatMessage({ id: 'option' });
  const selectedLabel = formatMessage({ id: 'selected' });
  const notSelectedLabel = formatMessage({ id: 'notSelected' });

  return (
    <PickerAspectGroupContainer>
      <ListCardGroup
        header={pickerAspect.name.locale}
        items={pickerAspect.pickerAspectOptions}
        renderItem={({
          item: option,
          index: optionIndex,
        }: {
          item: IPickerAspectOption;
          index: number;
        }) => {
          const isFirstPickerAspect = aspectIndex === FIRST_INDEX;
          const isSelected = selectedOption === option.identifier;

          const optionName = option.name?.locale ?? '';
          const optionDescription = option.description?.locale ?? '';
          const optionCalories = formatCalorieRange(
            getCalorieMapForCurrentPickerAspect(aspectIndex)?.[option.identifier]
          );

          // Aspect option accessibility compatibility
          const label = `${optionName}. ${optionDescription}. ${optionCalories}. ${optionLabel}`;
          const productAccessibilityLabel = Platform.select({
            // native ios and android:
            default: `${label}. ${isSelected ? selectedLabel : notSelectedLabel}.`,
            // mobile web, ios web, android web:
            web: label,
          });

          // Overrides product images with picker aspect option images
          const image = pickerAspect.pickerAspectImagesOverride
            ? option.image
            : imageForPickerAspectOption(option.identifier);

          const price = priceForPickerAspectOption?.(option.identifier);
          const badge = badgeForPickerAspectOption?.(option.identifier);

          return (
            <PickerListItem
              accessibilityLabel={productAccessibilityLabel}
              key={`${option.identifier}-${optionIndex}`}
              isClickable
              isSelected={isSelected}
              isFlippable={false}
              onProductClick={() => handleOnProductClick(option.identifier)}
              badgeContentLeft={
                badge ? (
                  <MenuAcceleratorBadge
                    bonusPoints={badge.bonusPoints}
                    pointsMultiplier={badge.pointsMultiplier}
                  />
                ) : null
              }
              productProps={{
                name: optionName,
                description: optionDescription,
                image: image ? JSON.stringify(image) : undefined,
                price,
                showPriceUpcharge: !isFirstPickerAspect,
                fallbackImage: showImages ? FallbackImageSvg : undefined,
                calories: optionCalories,
              }}
            />
          );
        }}
      />
    </PickerAspectGroupContainer>
  );
};
